import { useEffect } from 'react';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { refreshPlatformAccessToken } from '@/lib/platform-token-refresh';

/** Milliseconds before expiry at which to trigger a proactive refresh. */
const REFRESH_BEFORE_EXPIRY_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Decodes the `exp` claim from a JWT without verifying the signature.
 * Returns the expiry as a Unix timestamp (seconds), or null if unreadable.
 */
function parseJwtExp(token: string): number | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const json = atob(segment.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    return typeof payload['exp'] === 'number' ? payload['exp'] : null;
  } catch {
    return null;
  }
}

/**
 * Schedules a silent token refresh 2 minutes before the platform access token expires.
 *
 * - Arms a new timer whenever `accessToken` changes (i.e. after each rotation).
 * - Calls the same singleton `refreshPlatformAccessToken()` used by the 401 interceptor,
 *   so concurrent proactive + reactive attempts share one in-flight request.
 * - Safe to call unconditionally — does nothing when unauthenticated.
 */
export function usePlatformAutoRefresh(): void {
  const accessToken = usePlatformAuthStore((s) => s.accessToken);
  const isAuthenticated = usePlatformAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const exp = parseJwtExp(accessToken);
    if (exp === null) return;

    const msUntilExpiry = exp * 1000 - Date.now();
    const msUntilRefresh = msUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;

    if (msUntilRefresh <= 0) {
      // Token has already expired or is expiring imminently — refresh now.
      // The 401 interceptor may already be handling this; the singleton deduplicates.
      void refreshPlatformAccessToken().catch(() => {
        // clearAuth() already called inside refreshPlatformAccessToken on failure
      });
      return;
    }

    const timer = setTimeout(() => {
      void refreshPlatformAccessToken().catch(() => {
        // clearAuth() already called inside refreshPlatformAccessToken on failure
      });
    }, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [accessToken, isAuthenticated]);
}
